import type { If } from '../types/if';
import type { IsNotEqual } from '../types/isNotEqual';
import type { ToUnion } from '../types/toUnion';

type IsNotExhaustive<E extends string | number, CE extends E> =
    string extends E ? true
    : number extends E ? true
    : IsNotEqual<ToUnion<E>, ToUnion<CE>>;

export type WhenParameters<E extends string | number, CE extends E, R, F> = {
    expression: E;
    cases: Record<CE, R>;
} & If<IsNotExhaustive<E, CE>, { fallback: F }>;

/**
 * Switch with return value. Must be exhaustive or receive fallback.
 * @param expression Expression to evaluate.
 * @param cases Record that maps each possible value of 'expression' to a return value.
 * @param fallback Default case.
 */
export const when = <
    E extends string | number,
    CE extends E,
    const R,
    const F,
>({
    expression,
    cases,
    ...rest
}: WhenParameters<E, CE, R, F>): IsNotExhaustive<E, CE> extends true ? R | F
:   R => {
    if (expression in cases) {
        return cases[expression as CE];
    } else if ('fallback' in rest) {
        return rest.fallback as ReturnType<typeof when<E, CE, R, F>>;
    } else {
        throw new Error(
            `The expression did not match any case and fallback wasn't provided!`
        );
    }
};
